import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useWhyTramaContent} from '@/content/marketing-pages/whyTrama'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Text, TextVariant} from '../../content/Text'
import {ArrowIcon} from '../../icons/ArrowIcon'
import {WhyTramaComparisonTableDesktop} from './WhyTramaComparisonTableDesktop'
import {WhyTramaComparisonTableMobile} from './WhyTramaComparisonTableMobile'

type WhyTramaComparisonProps = {
  numOfRegisteredTrademarks: number
  hasCTA?: boolean
  country?: Jurisdiction
  buttonLinkHref?: string
}

export const WhyTramaComparison: FC<React.PropsWithChildren<WhyTramaComparisonProps>> = ({
  numOfRegisteredTrademarks,
  hasCTA = true,
  country,
  buttonLinkHref,
}) => {
  const {t} = useTranslation()
  const {title, description, buttonLink, buttonText} = useWhyTramaContent(country)

  return (
    <>
      <div className="header">
        <Text variant={TextVariant.h2} align="center">
          {t(title)}
        </Text>
        <div className="description">
          <Text variant={TextVariant.text20} align="center" color={theme.palette.text.light}>
            {t(description)}
          </Text>
        </div>
        {hasCTA && (
          <div className="button-wrapper">
            <Link
              href={buttonLinkHref || buttonLink}
              onClick={() =>
                analytics.track("Click on Start with free lawyer's check button in Why Trama table")
              }
            >
              <Button
                variant={ButtonVariant.primary}
                type="button"
                icon={<ArrowIcon color={theme.palette.text.white} />}
                isIconAnimated
              >
                {t(buttonText)}
              </Button>
            </Link>
          </div>
        )}
      </div>
      <div className="desktop">
        <WhyTramaComparisonTableDesktop
          numOfRegisteredTrademarks={numOfRegisteredTrademarks}
          country={country}
        />
      </div>
      <div className="mobile">
        <WhyTramaComparisonTableMobile
          numOfRegisteredTrademarks={numOfRegisteredTrademarks}
          country={country}
        />
      </div>

      <style jsx>{`
        .header {
          margin-bottom: ${theme.spacing[6.5]};
        }

        .description {
          margin-top: ${theme.spacing[2]};
        }

        .button-wrapper {
          text-align: center;
          margin-top: ${theme.spacing[3]};
        }

        .mobile {
          display: none;
        }

        @media ${MOBILE_SCREENSIZE} {
          .desktop {
            display: none;
          }

          .mobile {
            display: block;
          }
        }
      `}</style>
    </>
  )
}
